import { useMemo } from "react"
import ReactSelect, {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  SingleValue,
  SingleValueProps,
} from "react-select"
import classNames from "classnames"

import "./select.scss"

import { UITypes } from "types"
import { ReactComponent as IconSelect } from "assets/svg/icon-select.svg"
import { ReactComponent as IconTableCross } from "assets/svg/icon-table-cross.svg"

import useUrlQueryParams from "hooks/useUrlQueryParams"

import styles from "./index.module.scss"

const DropdownIndicatorComponent = (props: DropdownIndicatorProps<UITypes.Option>) => {
  return (
    <components.DropdownIndicator {...props}>
      <span
        className={classNames(styles.iconArrow, {
          [styles.open]: props?.selectProps?.menuIsOpen,
        })}
      >
        <IconSelect />
      </span>
    </components.DropdownIndicator>
  )
}

const ClearIndicator = (props: ClearIndicatorProps<UITypes.Option>) => {
  const { innerProps } = props
  return (
    <div {...innerProps} className={styles.clearIcon}>
      <IconTableCross />
    </div>
  )
}

const ColorOption = (props: OptionProps<UITypes.Option, false, GroupBase<UITypes.Option>>) => {
  const { data, innerRef, innerProps } = props

  return (
    <div ref={innerRef} {...innerProps} className={styles.colorOption}>
      <span style={{ color: data.color || "initial" }}>{data.label}</span>
    </div>
  )
}

const ColorSingleValue = (props: SingleValueProps<UITypes.Option, false, GroupBase<UITypes.Option>>) => {
  const { data } = props
  const color = data?.color || "initial"

  return (
    <components.SingleValue {...props} className={styles.colorSingleValue}>
      <span style={{ color }}>{data?.label}</span>
    </components.SingleValue>
  )
}

interface Props {
  headerCell: UITypes.TableHeaderCell
  isColorSelect?: boolean
}

export const TableSelect = ({ headerCell: { title, name, options }, isColorSelect = false }: Props) => {
  const { originalQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const value = useMemo(() => {
    if (options) {
      const result = options.filter(item => item.value === originalQueryParams[name || ""])
      return result[0]
    }
  }, [originalQueryParams, options, name])

  const onChange = (data: SingleValue<UITypes.Option>) => {
    if (!name) return

    setUrlQueryParams({
      [name]: data?.value || null,
    })
  }

  const customComponents = isColorSelect
    ? {
        Option: ColorOption,
        SingleValue: ColorSingleValue,
        DropdownIndicator: DropdownIndicatorComponent,
        ClearIndicator,
      }
    : {
        DropdownIndicator: DropdownIndicatorComponent,
        ClearIndicator,
      }

  return (
    <div className={classNames(styles.field)}>
      <ReactSelect
        isMulti={false}
        options={options}
        classNamePrefix="filter"
        value={value || null}
        isClearable={true}
        hideSelectedOptions={true}
        className="custom"
        name={name}
        placeholder={title}
        onChange={onChange}
        components={customComponents}
      />
    </div>
  )
}
